import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { Placeholder } from "rsuite";
import { Element } from "react-scroll";
import {
  StyleTypeContext,
  TranslationContext,
  handleNonValidImgFn,
} from "../../../umbilical_tools";
import sanitizeHtml from "sanitize-html";
import { withTripadvisor } from "../../../context";
import {
  TripAdvisorAwards,
  TripAdvisorWidget,
} from "../../../components/hotels/tripadvisor";
import { ShareBtn } from "../../../components/common/ShareBtn";
import { BottomContainer, Address } from "./helpers";
import { OfferDiscountTag } from "../../../components/offerDiscount";
import { useSelector } from "react-redux";
import jssVariables, { spacings } from "../../../jss_setup";
import { hotelsIsSearchingSelector } from "../../../umbilical_hocs/hotels_selector";
import RatingPanel from "../../../components/common/RatingPanel";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { rectifyHotelImages, valaquenta_api_url } from "../../../api";

export function fetchHotelSimpleMetaData(hotelId, supplier, language = "en") {
  return axios.post(
    `${valaquenta_api_url}/accommodator/hotels-metadata/simple`,
    { hotel_ids: [hotelId], supplier, language: language.toLowerCase() }
  );
}

const baseStyles = {
  hotel: (props) => ({
    position: "relative",
    display: "grid",
    gridTemplateRows: `${jssVariables.card_img_height_mobile} 1fr`,
    borderRadius: jssVariables.border_radius,
    boxShadow: jssVariables.shadows.smallShadow,
    width: `calc(100vw - (${spacings.mobile.medium} * 2))`,
    color: jssVariables.colors.text.dark,
    maxWidth: "60rem",
    justifySelf: "center",
    ..._.get(props, "customStyles.hotel", {}),
  }),
  imgContainer: (props) => ({
    width: "100%",
    height: jssVariables.card_img_height_mobile,
    position: "relative",
    borderRadius: [
      jssVariables.border_radius,
      jssVariables.border_radius,
      0,
      0,
    ],
    maxHeight: props.expandedDescription ? "16rem" : "unset",
    ..._.get(props, "customStyles.imgContainer", {}),
  }),
  img: (props) => ({
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: [
      jssVariables.border_radius,
      jssVariables.border_radius,
      0,
      0,
    ],
    ..._.get(props, "customStyles.img", {}),
  }),
  imgOverlay: (props) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 100,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0, 0, 0, 0) 60%)",
    transform: "matrix(1, 0, 0, -1, 0, 0)",
    ..._.get(props, "customStyles.imgOverlay", {}),
  }),
  placeholderImg: (props) => ({
    height: "100% !important",
    borderRadius: [
      jssVariables.border_radius,
      jssVariables.border_radius,
      0,
      0,
    ],
    ..._.get(props, "customStyles.placeholderImg", {}),
  }),
  placeholderParagraph: (props) => ({
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    ..._.get(props, "customStyles.placeholderParagraph", {}),
  }),
  ratingAndTitleDiv: (props) => ({
    display: "grid",
    height: "100%",
    alignContent: "end",
    position: "relative",
    padding: jssVariables.normal_gap,
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.ratingAndTitleDiv", {}),
  }),
  rateAndAddrAndTripAdv: (props) => ({
    display: "grid",
    gridTemplateColumns: props.withTripadvisor ? "1fr 1fr" : "1fr",
    ..._.get(props, "customStyles.rateAndAddrAndTripAdv", {}),
  }),
  titleInImg: (props) => ({
    fontSize: "x-large",
    fontWeight: "bold",
    textTransform: "uppercase",
    color: jssVariables.colors.text.light,
    ..._.get(props, "customStyles.titleInImg", {}),
  }),
  stripeBelowImage: (props) => ({
    height: `calc(${jssVariables.normal_gap} * .8)`,
    width: "100%",
    background: "gray",
    ..._.get(props, "customStyles.stripeBelowImage", {}),
  }),
  contentAndBtmContr: (props) => ({
    height: "100%",
    width: `calc(100vw - (${spacings.mobile.medium} * 2) )`,
    ..._.get(props, "customStyles.contentAndBtmContr", {}),
  }),
  header: (props) => ({
    display: "grid",
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    paddingBottom: 0,
    ..._.get(props, "customStyles.header", {}),
  }),
  title: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.title", {}),
  }),
  subtitle: (props) => ({
    fontSize: "medium",
    fontWeight: "bold",
    ..._.get(props, "customStyles.subtitle", {}),
  }),
  rateAndTrip: (props) => ({
    ..._.get(props, "customStyles.rateAndTrip", {}),
  }),
  content: (props) => ({
    padding: `0 calc(${jssVariables.normal_gap} * 1.5)`,
    ..._.get(props, "customStyles.content", {}),
  }),
  addressWithTripAdv: (props) => ({
    display: "grid",
    gridTemplateColumns: props.withTripadvisor ? "1fr 1fr" : "1fr",
    gridGap: jssVariables.half_gap,
    alignItems: "center",
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    paddingBottom: 0,
    ..._.get(props, "customStyles.addressWithTripAdv", {}),
  }),
  description: (props) => ({
    overflow: "hidden",
    [`-webkit-line-clamp`]: props.expandedDescription ? "1000!important" : 5,
    [`-webkit-box-orient`]: "vertical",
    display: props.expanedDescription ? "box" : "-webkit-box",
    margin: [jssVariables.normal_gap, 0],
    minHeight: "5rem",
    maxHeight: props.expandedDescription ? "inherit" : "5rem",
    height: "max-content",
    cursor: "pointer",
    ..._.get(props, "customStyles.description", {}),
    "& *": {
      fontSize: "small!important",
      height: "max-content!important",
    },
    "& h1, h2, h3, h4, h5, h6": {
      lineHeight: "1.5rem!important",
    },
  }),
  viewMoreText: (props) => ({
    color: "black",
    fontWeight: "bold",
    fontSize: "small",
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      color: "#6f6c6c",
      textDecoration: "none",
    },
    ..._.get(props, "customStyles.viewMoreText", {}),
  }),
};
const styles = createUseStyles({
  ...baseStyles,
  [`@media ${jssVariables.media.smallscreen}`]: {
    hotel: (props) => ({
      gridTemplateRows: "100%",
      gridTemplateColumns: `${jssVariables.card_img_width_desktop} 1fr`,
      boxShadow: jssVariables.shadows.smallShadow,
      width: "70vw",
      // borderRadius: jssVariables.border_radius,
      ..._.get(props, "customStyles.hotel.smallscreen", {}),
    }),
    imgContainer: (props) => ({
      height: "100%",
      borderRadius: [
        jssVariables.border_radius,
        0,
        0,
        jssVariables.border_radius,
      ],
      ..._.get(props, "customStyles.imgContainer.smallscreen", {}),
    }),
    img: (props) => ({
      // borderRadius: [
      //   jssVariables.border_radius,
      //   0,
      //   0,
      //   jssVariables.border_radius,
      // ],
      ..._.get(props, "customStyles.img.smallscreen", {}),
    }),
    imgOverlay: (props) => ({
      borderRadius: [
        jssVariables.border_radius,
        0,
        0,
        jssVariables.border_radius,
      ],
      ..._.get(props, "customStyles.imgOverlay.smallscreen", {}),
    }),
    stripeBelowImage: (props) => ({
      width: `calc(${jssVariables.normal_gap} * .8)`,
      height: "100%",
      background: "gray",
      ..._.get(props, "customStyles.stripeBelowImage", {}),
    }),
    placeholderImg: (props) => ({
      height: "100%!important",
      ..._.get(props, "customStyles.placeholderImg.smallscreen", {}),
    }),
    placeholderParagraph: (props) => ({
      gridColumn: "span 2",
      ..._.get(props, "customStyles.placeholderParagraph.smallscreen", {}),
    }),
    contentAndBtmContr: (props) => ({
      display: "grid",
      gridTemplateRows: "max-content 1fr",
      gridTemplateColumns: "1fr max-content",
      gridGap: 0,
      width: "100%",
      ..._.get(props, "customStyles.contentAndBtmContr.smallscreen", {}),
    }),
    header: (props) => ({
      gridColumn: "span 2",
      alignContent: "baseline",
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    content: (props) => ({
      padding: `calc(${jssVariables.normal_gap} * 1.5)`,
      width: "100%",
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    title: (props) => ({
      ..._.get(props, "customStyles.title.smallscreen", {}),
    }),
    description: (props) => ({
      margin: 0,
      [`-webkit-line-clamp`]: props.expandedDescription
        ? "1000!important"
        : props.showsDistanceInAddress
        ? 4
        : 5,
      ..._.get(props, "customStyles.description.smallscreen", {}),
    }),
  },
});
const mobileViewForDesktopStyles = createUseStyles({
  ...baseStyles,
  [`@media ${jssVariables.media.smallscreen}`]: {
    hotel: (props) => ({
      width: "100%!important",
      ..._.get(props, "customStyles.hotel.smallscreen", {}),
    }),
    imgContainer: (props) => ({
      width: "100%",
      height: "100%",
      position: "relative",
      ..._.get(props, "customStyles.imgContainer.smallscreen", {}),
    }),
    title: (props) => ({
      width: "100%!important",
      ..._.get(props, "customStyles.title.smallscreen", {}),
    }),
    contentAndBtmContr: (props) => ({
      display: "grid",
      gridTemplateRows: "max-content 1fr max-content",
      width: "100%!important",
      ..._.get(props, "customStyles.contentAndBtmContr.smallscreen", {}),
    }),
    placeholderImg: (props) => ({
      height: "100%!important",
      minWidth: "19rem!important",
      ..._.get(props, "customStyles.placeholderImg.smallscreen", {}),
    }),
  },
});
const Hotel = ({
  idx,
  ghost,
  hotel,
  tripadvisor_data,
  mobileViewOnly,
  isInLandingPage,
  withPrice,
  tripadvisorLoading,
  destinationName,
  setViewMapMode,
  setSingleHotelForMap,
  showBottomContainer,
  page_session_id,
  onViewHtlSearchForm,
}) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const { t, i18n } = useContext(TranslationContext);
  const lang = i18n.language.split("-")?.[0] ?? "en";

  const bar_saving = hotel?.computed_data?.bar_saving ?? 0;
  const currency = hotel?.computed_data?.currency ?? "";
  const hotelId = hotel?.metadata?.id;

  var { data: metadata, isLoading } = useQuery({
    queryKey: ["hotelMetaData", hotelId, lang],
    queryFn: () =>
      hotelId
        ? fetchHotelSimpleMetaData(
            hotelId,
            hotel?.metadata?.data_supplier ?? "hotelston",
            lang
          )
        : new Promise((resolve) => resolve(null)),
    refetchOnWindowFocus: false,
  });
  metadata = metadata?.data?.hotels[0];

  const ghostOrLoading = ghost || isLoading;

  const offer = {
    amount: bar_saving,
    type: "NUM", //"PERC","NUM",
    currency,
    description: "",
    label: "save",
  };

  const { isSearching, has_bar_comparison, market_type } = useSelector(
    (state) => {
      return {
        isSearching: hotelsIsSearchingSelector(state),
        has_bar_comparison:
          state.WebsiteMetadata?.metadata?.has_bar_comparison ?? false,
        market_type: state.HotelSearch.searchPayload?.market_type || "B2C",
      };
    }
  );

  const customStyles = {
    ...cStyler({
      type,
      brand,
      withTripadvisor,
      showsDistanceInAddress: hotel?.computed_data?.distanceFromSelection ?? 0,
      objName: isInLandingPage ? "landingPageHotelStyles" : "hotelStyles",
    }),
  };

  const memoizedShowHotelOnMap = useCallback(() => {
    if (
      typeof setViewMapMode !== "function" ||
      typeof setSingleHotelForMap !== "function"
    ) {
      return;
    }
    setViewMapMode();
    setSingleHotelForMap([{ ...hotel }]);
  }, [isSearching, hotelId]);

  const classes = mobileViewOnly
    ? mobileViewForDesktopStyles({
        customStyles,
        type,
        ghost: ghostOrLoading,
        withTripadvisor: withTripadvisor && !_.isEmpty(tripadvisor_data),
        mobileViewOnly,
        expandedDescription,
      })
    : styles({
        customStyles,
        type,
        ghost: ghostOrLoading,
        withTripadvisor: withTripadvisor && !_.isEmpty(tripadvisor_data),
        expandedDescription,
      });

  const AddressComp = (
    <Address
      address={_.get(metadata, "address", "")}
      distanceFromSelection={hotel?.computed_data?.distanceFromSelection ?? 0}
      showHotelOnMap={memoizedShowHotelOnMap}
    />
  );

  const RatingComp = (
    <RatingPanel
      rating={_.get(metadata, "rating", 0)}
      color={
        brand === "rocs"
          ? jssVariables.colors.text.light
          : jssVariables.colors.base_color
      }
      fontSize={brand === "rocs" && mobileViewOnly ? "large" : "x-large"}
      customStyles={customStyles?.RatingPanel ?? {}}
    />
  );

  const availableProviders = hotel?.computed_data?.available_providers || [];

  return (
    <Element
      name={(hotel?.metadata?.id ?? "").toString()}
      className={classes.hotel}>
      {ghostOrLoading ? (
        <Placeholder.Graph active className={classes.placeholderImg} />
      ) : (
        <div className={classes.imgContainer}>
          <img
            className={classes.img}
            src={metadata?.images?.[0]?.url ?? ""}
            alt=""
            onError={async (e) => {
              const img_url = metadata?.images?.[0]?.url;
              handleNonValidImgFn(e);

              try {
                const result = await rectifyHotelImages({
                  img_url,
                  hotel_uid: metadata?.id,
                  supplier: metadata?.data_supplier,
                });
                const working_images = result?.data?.working_images || [];
                if (!working_images.length) {
                  handleNonValidImgFn(e);
                  return;
                }
                e.target.src = working_images?.[0];
              } catch (error) {
                handleNonValidImgFn(e);
              }
            }}
          />
          <div className={classes.imgOverlay} />
          {type === "type4" && (
            <div className={classes.ratingAndTitleDiv}>
              {RatingComp}
              <div className={classes.titleInImg}>
                {_.startCase(_.toLower(hotel.metadata.name))}
              </div>
            </div>
          )}
          {withTripadvisor && <TripAdvisorAwards data={tripadvisor_data} />}
          {isInLandingPage && (
            <ShareBtn
              placeBtmRight={true}
              path={`/deep-hotel/${hotel.reference}`}
            />
          )}
          {bar_saving > 0 && has_bar_comparison && (
            <OfferDiscountTag offer={offer} />
          )}
        </div>
      )}
      {type === "type4" && <div className={classes.stripeBelowImage}></div>}
      <div className={classes.contentAndBtmContr}>
        {ghostOrLoading ? (
          <Placeholder.Paragraph
            rows={5}
            active
            className={classes.placeholderParagraph}
          />
        ) : (
          <React.Fragment>
            {["type1", "type2", "type3"].includes(type) && (
              <div className={classes.header}>
                <div className={classes.title}>{hotel.metadata.name}</div>
                <div className={classes.subtitle}>{destinationName}</div>
                <div className={classes.rateAndAddrAndTripAdv}>
                  <div className={classes.rateAndTrip}>
                    {RatingComp}
                    {AddressComp}
                    {market_type === "B2B" && (
                      <span>
                        <strong>Available Providers:</strong>{" "}
                        {availableProviders
                          .map((pr) => _.capitalize(pr))
                          .join(", ")}
                      </span>
                    )}
                  </div>
                  {withTripadvisor && (
                    <TripAdvisorWidget
                      data={tripadvisor_data}
                      loading={tripadvisorLoading}
                    />
                  )}
                </div>
              </div>
            )}
            {brand === "rocs" && (
              <div className={classes.addressWithTripAdv}>
                {AddressComp}
                {withTripadvisor && (
                  <TripAdvisorWidget
                    data={tripadvisor_data}
                    loading={tripadvisorLoading}
                  />
                )}
              </div>
            )}
            <div className={classes.content}>
              {metadata?.description ? (
                <React.Fragment>
                  <p
                    className={classes.description}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(_.get(metadata, "description", "")),
                    }}
                    onClick={() => setExpandedDescription((p) => !p)}
                  />
                  <a
                    className={classes.viewMoreText}
                    id={
                      expandedDescription
                        ? `viewLess${idx + 1}`
                        : `viewMore${idx + 1}`
                    }
                    onClick={() => setExpandedDescription((p) => !p)}>
                    {t(expandedDescription ? "viewLess" : "viewMore")}
                  </a>
                </React.Fragment>
              ) : (
                <p className={classes.description}>
                  {sanitizeHtml(_.get(tripadvisor_data, "description", ""), {
                    allowedTags: [],
                  })}
                </p>
              )}
            </div>
          </React.Fragment>
        )}
        {showBottomContainer && (
          <BottomContainer
            idx={idx}
            hotel={hotel}
            mobileViewOnly={mobileViewOnly}
            withPrice={withPrice}
            ghost={ghostOrLoading}
            page_session_id={page_session_id}
            onViewHtlSearchForm={onViewHtlSearchForm}
          />
        )}
      </div>
    </Element>
  );
};
Hotel.defaultProps = {
  ghost: false,
  hotel: null,
  tripadvisor_data: null,
  mobileViewOnly: false,
  isInLandingPage: false,
  withPrice: true,
  tripadvisorLoading: false,
  destinationName: "",
  page_session_id: "",
  showBottomContainer: true,
  idx: 0,
};
Hotel.propTypes = {
  ghost: PropTypes.bool,
  hotel: PropTypes.object,
  tripadvisor_data: PropTypes.object,
  mobileViewOnly: PropTypes.bool,
  isInLandingPage: PropTypes.bool,
  withPrice: PropTypes.bool,
  tripadvisorLoading: PropTypes.bool,
  destinationName: PropTypes.string,
  setViewMapMode: PropTypes.func,
  setSingleHotelForMap: PropTypes.func,
  showBottomContainer: PropTypes.bool,
  page_session_id: PropTypes.string,
  onViewHtlSearchForm: PropTypes.func,
  idx: PropTypes.number,
};

export default Hotel;
